/**
 * Booth codes: a number plus an optional auxiliary letter ("45", "45A").
 * Same parsing rule as the API (apps/api services/booths.ts parseCode), so
 * "45 a", "45-A" and "०४५" all mean booth 45 / 45A.
 */

const DEVANAGARI_DIGITS = "०१२३४५६७८९";

/** Hindi keyboards can type Devanagari digits; the API only understands 0-9. */
export function normalizeDigits(s: string): string {
  return s.replace(/[०-९]/g, (d) => String(DEVANAGARI_DIGITS.indexOf(d)));
}

export interface ParsedCode {
  no: number;
  aux: string;
  code: string;
}

export function parseBoothCode(input: string): ParsedCode | null {
  const m = /^\s*(\d{1,4})\s*-?\s*([A-Za-z]?)\s*$/.exec(normalizeDigits(input ?? ""));
  if (!m || Number(m[1]) <= 0) return null;
  const no = Number(m[1]);
  const aux = m[2].toUpperCase();
  return { no, aux, code: `${no}${aux}` };
}

interface BoothLike {
  code: string;
  no: number;
  name: string;
  mandal?: string | null;
  panchayat?: string | null;
  village?: string | null;
}

/** Exact match for what the surveyor typed ("45" → booth 45, "45a" → booth 45A). */
export function findBoothByCode<B extends BoothLike>(booths: readonly B[], input: string): B | null {
  const p = parseBoothCode(input);
  if (!p) return null;
  return booths.find((b) => b.code === p.code) ?? null;
}

/**
 * List filter: digits match booth numbers by prefix ("4" → 4, 40-49, 400...),
 * "45A" matches that code, anything else searches name / mandal / panchayat / village.
 */
export function filterBooths<B extends BoothLike>(booths: readonly B[], input: string): B[] {
  const q = normalizeDigits((input ?? "").trim());
  if (!q) return [...booths];
  const p = parseBoothCode(q);
  if (p) {
    if (p.aux) return booths.filter((b) => b.code.startsWith(p.code));
    const digits = String(p.no);
    return booths.filter((b) => String(b.no).startsWith(digits));
  }
  const needle = q.toLowerCase();
  return booths.filter((b) =>
    [b.name, b.mandal, b.panchayat, b.village].some((v) => (v ?? "").toLowerCase().includes(needle))
  );
}
